import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate, formatTime } from "@/lib/dateFormat";
import { TrendingUp, Users, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const STATUS_COLORS = { on_time: "#22C55E", late: "#F97316", absent: "#EF4444" };
const STATUS_LABELS = { on_time: "On-time", late: "Late", absent: "Absent" };
const LATE_THRESHOLD = "10:30"; // matches backend _ATTENDANCE_LATE_THRESHOLD

function isLateCheckin(checkIn) {
  if (!checkIn || checkIn.length < 16) return false;
  return checkIn.slice(11, 16) > LATE_THRESHOLD;
}

/** Segment label inside a stacked bar — mirrors LeavesInsights' makeSegmentLabel. */
function makeSegmentLabel(type, rows) {
  return function SegmentLabel(props) {
    const { x, y, width, height, index } = props;
    const row = rows[index];
    if (!row || !row[type] || width < 22) return null;
    return (
      <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={600} fill="#fff">
        {row[type]}
      </text>
    );
  };
}

/** Founder/admin-only analytics panel for the Attendance page — team
 * on-time/late/absent distribution, monthly trends, monthly late count,
 * and a per-person daily breakdown. Styled 1:1 with LeavesInsights.jsx.
 * `rows` (last ~30 days of attendance, already fetched by Attendance.jsx)
 * powers the individual daily breakdown so we don't double-fetch. */
export default function AttendanceInsights({ rows, onModify }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState("all");

  const load = async (y) => {
    setLoading(true);
    try {
      const { data: insights } = await api.get("/attendance/insights", { params: { year: y } });
      setData(insights);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(year); }, [year]);

  const teamChartData = useMemo(() => (data?.team_distribution || []).map((r) => ({
    name: r.name, user_id: r.user_id, on_time: r.on_time, late: r.late,
    absent: r.absent, monthly_late_count: r.monthly_late_count,
  })), [data]);

  // Phase 98 fix — the "Monthly Late Count" table must ONLY ever reflect the
  // current calendar month (was reusing the yearly on_time/late/absent
  // totals above, so the row could add up to way more than the ~20 working
  // days in a month). Uses the backend's separate month-scoped fields.
  const monthlyTableData = useMemo(() => (data?.team_distribution || []).map((r) => ({
    name: r.name, user_id: r.user_id,
    on_time: r.month_on_time, late: r.month_late, absent: r.month_absent,
    monthly_late_count: r.month_late,
  })), [data]);

  const peopleForSelect = data?.team_distribution || [];

  const monthlyChartData = useMemo(() => {
    if (selectedUser !== "all") return data?.monthly_by_user?.[selectedUser] || [];
    return data?.monthly_distribution || [];
  }, [data, selectedUser]);

  const individualRows = useMemo(() => {
    if (selectedUser === "all") return [];
    return (rows || []).filter((r) => r.user_id === selectedUser).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [rows, selectedUser]);

  return (
    <div className="space-y-6" data-testid="attendance-insights">
      <div className="flex items-center justify-between">
        <div className="label-overline">Insights · {loading ? "loading…" : year}</div>
        <div className="inline-flex items-center gap-1">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                y === year ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`attendance-insights-year-${y}`}
            >{y}</button>
          ))}
        </div>
      </div>

      {/* Team Attendance Distribution */}
      <div className="card-flat p-4" data-testid="chart-attendance-distribution">
        <div className="text-sm font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5"><Users size={14} />Team attendance distribution</div>
        <div className="text-xs text-slate-500 mb-3">
          On-time vs late (check-in after 10:30 AM) vs absent working days this year, per person — highest lateness/absence first. Days on approved leave are excluded.
        </div>
        <ResponsiveContainer width="100%" height={Math.max(220, teamChartData.length * 34)}>
          <BarChart data={teamChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} fontSize={11} />
            <YAxis type="category" dataKey="name" width={120} fontSize={11} />
            <Tooltip formatter={(v, n) => [v, STATUS_LABELS[n] || n]} />
            <Legend formatter={(v) => STATUS_LABELS[v] || v} wrapperStyle={{ fontSize: 11 }} />
            {Object.keys(STATUS_COLORS).map((s) => (
              <Bar key={s} dataKey={s} stackId="a" fill={STATUS_COLORS[s]} name={s}>
                <LabelList dataKey={s} content={makeSegmentLabel(s, teamChartData)} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
        {teamChartData.length === 0 && !loading && <div className="text-xs text-slate-400 italic py-4 text-center">No attendance data in {year} yet.</div>}
      </div>

      {/* Monthly Trends + Person selector (Individual Breakdown) */}
      <div className="card-flat p-4" data-testid="chart-attendance-monthly">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5"><TrendingUp size={14} />Monthly trends</div>
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-56" data-testid="attendance-insights-person-select"><SelectValue placeholder="Everyone" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone (team total)</SelectItem>
              {peopleForSelect.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 mb-3">
          {selectedUser === "all" ? "Team-wide on-time / late / absent counts, month by month." : "Selected person's on-time / late / absent days, month by month."}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthlyChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" fontSize={11} />
            <YAxis allowDecimals={false} fontSize={11} />
            <Tooltip formatter={(v, n) => [v, STATUS_LABELS[n] || n]} />
            <Legend formatter={(v) => STATUS_LABELS[v] || v} wrapperStyle={{ fontSize: 11 }} />
            {Object.keys(STATUS_COLORS).map((s) => (
              <Bar key={s} dataKey={s} stackId="a" fill={STATUS_COLORS[s]} name={s} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {monthlyChartData.length === 0 && !loading && <div className="text-xs text-slate-400 italic py-4 text-center">No data for {year}.</div>}

        {selectedUser !== "all" && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="individual-attendance-list">
            <div className="label-overline mb-2">
              {peopleForSelect.find((p) => p.user_id === selectedUser)?.name || "This person"}&rsquo;s recent days ({individualRows.length})
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {individualRows.map((r) => {
                const late = isLateCheckin(r.check_in);
                return (
                  <div key={r.id} className="flex items-center justify-between gap-2 text-xs py-1.5" data-testid={`individual-attendance-${r.id}`}>
                    <span className="text-slate-700 w-20 shrink-0">{formatDate(r.date)}</span>
                    <span className="font-mono text-slate-600 flex-1">{formatTime(r.check_in)} → {formatTime(r.check_out)}</span>
                    <Tag tone={late ? "orange" : (r.check_in ? "green" : "slate")}>{late ? "Late" : (r.check_in ? "On-time" : "—")}</Tag>
                    {onModify && (
                      <button type="button" onClick={() => onModify(r)} className="text-slate-400 hover:text-slate-700" title="Modify this attendance record" data-testid={`individual-attendance-modify-${r.id}`}>
                        <Pencil size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
              {individualRows.length === 0 && <div className="text-xs text-slate-400 italic py-3">No attendance records in the last 30 days.</div>}
            </div>
          </div>
        )}
      </div>

      {/* Monthly Late Count table */}
      {monthlyTableData.length > 0 && (
        <div className="card-flat overflow-hidden" data-testid="attendance-late-table">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="label-overline inline-flex items-center gap-1.5"><Clock size={12} />Monthly late count · {new Date().toLocaleString("en-US", { month: "long" })}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-5 py-2 label-overline">Member</th>
                  <th className="px-3 py-2 label-overline">On-time</th>
                  <th className="px-3 py-2 label-overline">Late</th>
                  <th className="px-3 py-2 label-overline">Absent</th>
                  <th className="px-3 py-2 label-overline">Monthly late count</th>
                </tr>
              </thead>
              <tbody>
                {monthlyTableData.map((r) => (
                  <tr key={r.user_id} className={`border-t border-slate-100 hover-row ${r.monthly_late_count >= 3 ? "bg-orange-50/60" : ""}`} data-testid={`attendance-late-row-${r.user_id}`}>
                    <td className="px-5 py-2 text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-green-700">{r.on_time}</td>
                    <td className="px-3 py-2 font-mono text-orange-600">{r.late}</td>
                    <td className="px-3 py-2 font-mono text-red-600">{r.absent}</td>
                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{r.monthly_late_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
